import { useState } from 'react';
import { useRecoilValue } from 'recoil';

import { Heading } from '@/components/heading';
import { JourneyLegTab } from '@/components/modules/journey/leg-tab';
import { Text } from '@/components/text';
import { TabsList, TabsRoot, TabsTrigger } from '@/components/ui/tabs';
import { getPortLineSequence } from '@/lib/models/lines/utils/get-port-line-sequence';
import { OptionModel } from '@/lib/models/option/types';
import { getLinkSegments } from '@/lib/models/segment/utils/get-link-segments';
import { linesState } from '@/state/lines';
import { locationsState } from '@/state/locations';
import { tv } from '@/utils/styles';

import { LineDetails } from '../details';
import { LineMap } from '../map';

export interface LineOverviewProps {
  option: OptionModel;
}

const LineOverview = ({ option }: LineOverviewProps) => {
  const [selectedSegmentId, setSelectedSegmentId] = useState(option.segments[0].id);
  const locations = useRecoilValue(locationsState);
  const lines = useRecoilValue(linesState);

  const linkSegments = getLinkSegments(option.segments);
  const selectedSegment = linkSegments.find((s) => s.id === selectedSegmentId);

  if (!selectedSegment) return null;

  const segmentStopLocationIds = selectedSegment.events.flat().map((e) => e.locationId) || [];
  const optionPortIds = [selectedSegment.fromId, ...segmentStopLocationIds, selectedSegment.toId];
  const lineId = selectedSegment?.lineIds?.[0] || option.lineIds[0];
  const selectedLine = lineId ? lines[lineId] : undefined;
  const linePortIds = selectedLine?.ports;

  if (!lineId) return null;

  // List of the ports visited in sequence
  const portSequence = getPortLineSequence(linePortIds, optionPortIds) || [];

  // List of the ports in the service
  const linePortLocations = (linePortIds || []).map((locationId) => locations[locationId]);

  const { base, header, headingText, subHeadingText, lineView, lineDetails, lineMap, lineTabs, tabTabsWrapper } =
    styles();

  return (
    <div className={base()}>
      <div className={header()}>
        <Heading variant="h2" className={headingText()}>
          Service overview
        </Heading>
        <Text variant="secondary" className={subHeadingText()}>
          Service line details including port rotation, VSA&apos;s and scheduled vessels.
        </Text>
      </div>
      {linkSegments.length > 1 && (
        <div className={lineTabs()}>
          <TabsRoot
            defaultValue={selectedSegment.id}
            value={selectedSegmentId}
            onValueChange={setSelectedSegmentId}
            className="relative"
          >
            <div className={tabTabsWrapper()}>
              <TabsList>
                {linkSegments.map((segment, index) => {
                  const { fromId, toId } = segment;
                  return (
                    <TabsTrigger value={segment.id} key={segment.id}>
                      <JourneyLegTab index={index} fromId={fromId} toId={toId} multipleLegString="Leg " />
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>
          </TabsRoot>
        </div>
      )}
      {selectedLine && (
        <div className={lineView()}>
          <div className={lineDetails()}>
            <LineDetails
              line={selectedLine}
              lineLocations={linePortLocations}
              portSequence={portSequence}
              vehicles={selectedSegment.vehicles}
            />
          </div>
          <div className={lineMap()}>
            <LineMap lineLocations={linePortLocations} portSequence={portSequence} />
          </div>
        </div>
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'mt-2 min-h-[30vh] border-t border-grey-200 bg-white py-8',
    header: 'mb-6 max-w-[760px] px-8',
    headingText: 'm-0 mb-1 !text-md leading-tight',
    subHeadingText: 'text-grey-600',
    lineHeader: 'mb-2 mt-3',
    lineName: 'm-0 font-semibold italic',
    lineView: 'relative mx-4 h-[75vh]',
    lineTabs: 'relative px-8',
    tabTabsWrapper: 'mb-6',
    lineDetails:
      'absolute -bottom-1 -top-1 left-0 z-2 w-[30%] rounded-md border border-grey-200 bg-white p-6 pt-2 shadow-md',
    lineMap: 'ml-[30%] h-full',
  },
});

export { LineOverview };
